import { Controller } from "@hotwired/stimulus"
import { openChrome, closeChrome, openOverlayScrim } from "../helpers/overlay_chrome"

// The one-time Help intro (budgets/_help_intro).
//
// budgets_controller#flag_help_intro says the card is eligible; the card
// decides the rest, exactly like goal_reached_controller: it waits for the page
// to settle, stands down if another overlay is already up, and the moment it is
// really on screen posts budgets#help_intro_seen so it never shows again, even
// if the app is closed with it open.
//
// With auto set to false the card waits for a tap instead (the Banks screen's
// trial popup, plaid/_trial_limit): the controller then sits on a wrapper
// holding both the button and the card, and opens as often as it is tapped.
//
// Shell copied from goal_reached_controller: the raised-attribute guard against
// a restored Turbo snapshot re-raising a card the server has already consumed,
// the scroll lock, and the native chrome hand-off through helpers/overlay_chrome.
export default class extends Controller {
  static targets = ["panel", "haptic"]
  static values = { seenUrl: String, auto: { type: Boolean, default: true } }

  static RAISED_ATTRIBUTE = "data-help-intro-raised"
  static SHOW_DELAY = 600
  static LAND_DELAY = 420

  connect() {
    this._timers = []
    this._resetForCache = () => this._reset()
    document.addEventListener("turbo:before-cache", this._resetForCache)

    if (!this.autoValue) return
    if (this.element.hasAttribute(this.constructor.RAISED_ATTRIBUTE)) return

    this._timers.push(setTimeout(() => {
      if (!openOverlayScrim()) this.show()
    }, this.constructor.SHOW_DELAY))
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this._resetForCache)
    this._timers.forEach((id) => clearTimeout(id))
    this._unlockScroll()
  }

  show() {
    this.element.setAttribute(this.constructor.RAISED_ATTRIBUTE, "1")
    this._markSeen()
    this.panelTarget.hidden = false
    this._lockScroll()
    openChrome(this, this.element)
    // Same double-rAF re-assert the celebration popups use: the budget
    // carousel calls bridge.show() for its first slide during load, which
    // would clobber an immediate hide().
    requestAnimationFrame(() => requestAnimationFrame(() => openChrome(this, this.element)))

    this._timers.push(setTimeout(() => {
      this.panelTarget.classList.add("is-landed")
      this._fireHaptic()
    }, this.constructor.LAND_DELAY))
  }

  // The X and the CTA both end here. Seen was already stamped on show, so
  // closing writes nothing.
  close() {
    this.panelTarget.hidden = true
    this.panelTarget.classList.remove("is-landed")
    this._unlockScroll()
    closeChrome(this)
    document.dispatchEvent(new CustomEvent("celebration:closed"))
  }

  // turbo:before-cache: hidden, unlocked, and still marked raised.
  _reset() {
    this.panelTarget.hidden = true
    this._unlockScroll()
  }

  // The one write. A request that never lands just means the card shows once
  // more next time, which beats never.
  _markSeen() {
    if (!this.hasSeenUrlValue) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.seenUrlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": token, Accept: "application/json" }
    }).catch(() => {})
  }

  _fireHaptic() {
    if (!this.hasHapticTarget) return
    const haptic = this.application.getControllerForElementAndIdentifier(this.hapticTarget, "bridge--haptic")
    haptic?.tap()
  }

  _lockScroll() {
    document.documentElement.style.overflow = "hidden"
  }

  _unlockScroll() {
    document.documentElement.style.overflow = ""
  }
}
